import { DepositInvoiceChoice, DepositAllocationSummary } from '../components/DepositInvoicePayment';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { depositApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Vault, ShieldCheck, Wallet, CheckCircle, WarningCircle, XCircle,
  FileArrowDown, Gavel, ArrowsClockwise,
} from '@phosphor-icons/react';

const STATUS_META = {
  hinterlegt: { label: 'Hinterlegt', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Vault },
  pending_review: { label: 'Schadenprüfung', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: ShieldCheck },
  pending_refund: { label: 'Rückzahlung ausstehend', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: Wallet },
  released: { label: 'Freigegeben', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  partial_retained: { label: 'Teilweise einbehalten', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: WarningCircle },
  fully_retained: { label: 'Vollständig einbehalten', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
};

const euro = (v) => `€ ${Number(v || 0).toLocaleString('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const Deposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [overview, setOverview] = useState({ buckets: {} });
  const [loading, setLoading] = useState(true);
  const [activeStatus, setActiveStatus] = useState('all');
  const [settleTarget, setSettleTarget] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const [settleForm, setSettleForm] = useState({
    retained_amount: 0,
    reason: '',
    invoice_id: '',
    create_invoice: true,
  });

  const [refundForm, setRefundForm] = useState({
    payment_method: 'bar',
    notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [depRes, ovRes] = await Promise.all([
        depositApi.getAll({}),
        depositApi.overview(),
      ]);

      const list = (depRes.data || []).map((d) => ({
        ...d,
        status: d.status === 'zurückgegeben'
          ? 'released'
          : (d.status || 'hinterlegt'),
      }));

      setDeposits(list);
      setOverview(ovRes.data || { buckets: {} });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (activeStatus === 'all') return deposits;
    return deposits.filter((d) => d.status === activeStatus);
  }, [deposits, activeStatus]);

  const handleMarkReview = async (dep) => {
    setBusy(true);
    try {
      await depositApi.markReview(dep.id);
      toast.success('Kaution zur Schadenprüfung übergeben');
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openSettle = (dep) => {
    setSettleTarget(dep);
    setSettleForm({
      retained_amount: 0,
      reason: '',
      invoice_id: '',
    create_invoice: true,
    });
  };

  const submitSettle = async () => {
    if (!settleTarget) return;

    const retained = Math.max(
      0,
      Math.min(
        parseFloat(settleForm.retained_amount) || 0,
        Number(settleTarget.amount || 0)
      )
    );

    setBusy(true);

    try {
      const res = await depositApi.settle(settleTarget.id, {
        retained_amount: retained,
        reason: settleForm.reason,
        create_invoice: settleForm.invoice_id ? false : settleForm.create_invoice,
        invoice_id: settleForm.invoice_id || null,
      });

      toast.success('Entscheidung gespeichert', {
        description: settleForm.invoice_id ? `${euro(retained)} auf Rechnung ${res.data.invoice_number} verrechnet` : res.data.invoice_number
          ? `Rechnung ${res.data.invoice_number} erzeugt`
          : retained === 0
            ? 'Rückzahlung ausstehend'
            : `Einbehalt € ${retained.toFixed(2)}`,
      });

      setSettleTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openRefund = (dep) => {
    setRefundTarget(dep);
    setRefundForm({
      payment_method: dep.payment_method || 'bar',
      notes: '',
    });
  };

  const submitRefund = async () => {
    if (!refundTarget) return;

    setBusy(true);

    try {
      await depositApi.completeRefund(refundTarget.id, refundForm);
      toast.success('Rückzahlung dokumentiert');
      setRefundTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadStatement = async (dep) => {
    try {
      const res = await depositApi.getStatementPdf(dep.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `kaution_${dep.id.slice(0, 8)}.pdf`;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const kpiKeys = [
    'hinterlegt',
    'pending_review',
    'pending_refund',
    'released',
    'partial_retained',
    'fully_retained',
  ];

  return (
    <div className="p-6 space-y-6" data-testid="deposits-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418]">
            Kautionsübersicht
          </h1>
          <p className="text-[#525866] mt-1">
            Automatischer Workflow: Buchung beendet → Schadenprüfung → Freigabe / Einbehalt → Rückzahlung
          </p>
        </div>

        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="deposits-refresh-btn"
        >
          <ArrowsClockwise weight="bold" className="mr-2" size={16} />
          Aktualisieren
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <button
          type="button"
          onClick={() => setActiveStatus('all')}
          className={`stat-card !p-4 text-left transition ${
            activeStatus === 'all' ? 'ring-2 ring-[#111418]' : ''
          }`}
        >
          <p className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-bold">
            Alle
          </p>
          <p className="text-2xl font-bold">{deposits.length}</p>
        </button>

        {kpiKeys.map((key) => {
          const meta = STATUS_META[key];
          const bucket = overview.buckets?.[key] || {
            count: 0,
            amount_total: 0,
          };
          const Icon = meta.icon;

          return (
            <button
              type="button"
              key={key}
              onClick={() => setActiveStatus(key)}
              className={`stat-card !p-4 text-left transition ${
                activeStatus === key ? 'ring-2 ring-[#111418]' : ''
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon weight="bold" size={16} className="text-[#525866]" />
                <p className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-bold">
                  {meta.label}
                </p>
              </div>

              <p className="text-2xl font-bold">{bucket.count || 0}</p>
              <p className="text-xs text-[#525866] mt-0.5">
                {euro(bucket.amount_total)}
              </p>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="card-swiss p-12 text-center text-[#525866]">
          Lade …
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-swiss p-12 text-center text-[#525866]">
          Keine Kautionen in diesem Status
        </div>
      ) : (
        <div className="card-swiss overflow-hidden">
          <table className="table-swiss">
            <thead>
              <tr>
                <th>Kunde</th>
                <th>Betrag</th>
                <th>Status</th>
                <th>Einbehalt / Rückzahlung</th>
                <th>Datum</th>
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((d) => {
                const meta = STATUS_META[d.status] || STATUS_META.hinterlegt;
                const StatusIcon = meta.icon;
                const cust = d.customer || {};

                return (
                  <tr key={d.id}>
                    <td>
                      <Link
                        to={`/customers/${d.customer_id}`}
                        className="font-semibold text-[#111418] hover:underline"
                      >
                        {cust.first_name} {cust.last_name}
                      </Link>

                      {d.booking_id && (
                        <div className="text-xs text-[#8A94A6]">
                          <Link
                            to={`/bookings/${d.booking_id}`}
                            className="hover:underline"
                          >
                            Buchung öffnen
                          </Link>
                        </div>
                      )}
                    </td>

                    <td>
                      <div className="font-bold">{euro(d.amount)}</div>
                      <div className="text-xs text-[#8A94A6]">
                        {(d.payment_method || '—').toUpperCase()}
                      </div>
                    </td>

                    <td>
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border ${meta.color}`}
                      >
                        <StatusIcon weight="fill" size={12} />
                        {meta.label}
                      </span>
                    </td>

                    <td className="text-sm">
                      {d.settled_at ? (
                        <>
                          <div>
                            Einbehalt:{' '}
                            <span className="font-semibold">
                              {euro(d.retained_amount)}
                            </span>
                          </div>
                          <div>
                            Rückzahlung:{' '}
                            <span className="font-semibold">
                              {euro(d.refund_amount)}
                            </span>
                          </div>
                          <DepositAllocationSummary deposit={d} />
                          {d.retention_invoice_number && (
                            <div className="text-xs text-[#8A94A6]">
                              Rechnung {d.retention_invoice_number}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-[#8A94A6]">—</span>
                      )}
                    </td>

                    <td className="text-xs text-[#525866]">
                      Erhalten:{' '}
                      {d.created_at
                        ? new Date(d.created_at).toLocaleDateString('de-DE')
                        : '—'}

                      {d.settled_at && (
                        <div>
                          Geprüft:{' '}
                          {new Date(d.settled_at).toLocaleDateString('de-DE')}
                        </div>
                      )}

                      {d.refund_completed_at && (
                        <div>
                          Abgeschlossen:{' '}
                          {new Date(d.refund_completed_at).toLocaleDateString('de-DE')}
                        </div>
                      )}
                    </td>

                    <td className="text-right whitespace-nowrap space-x-1">
                      {d.status === 'hinterlegt' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkReview(d)}
                          disabled={busy}
                        >
                          <ShieldCheck weight="bold" size={14} className="mr-1" />
                          Prüfen
                        </Button>
                      )}

                      {d.status === 'pending_review' && (
                        <Button
                          size="sm"
                          onClick={() => openSettle(d)}
                          disabled={busy}
                        >
                          <Gavel weight="bold" size={14} className="mr-1" />
                          Entscheiden
                        </Button>
                      )}

                      {d.status === 'pending_refund' && (
                        <Button
                          size="sm"
                          onClick={() => openRefund(d)}
                          disabled={busy}
                        >
                          <Wallet weight="bold" size={14} className="mr-1" />
                          Rückzahlen
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadStatement(d)}
                        title="Kautionsschreiben herunterladen"
                      >
                        <FileArrowDown weight="bold" size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={!!settleTarget}
        onOpenChange={(open) => !open && setSettleTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kautionsentscheidung</DialogTitle>
          </DialogHeader>

          {settleTarget && (
            <div className="space-y-4">
              <p className="text-sm text-[#525866]">
                Kaution{' '}
                <span className="font-semibold text-[#111418]">
                  {euro(settleTarget.amount)}
                </span>{' '}
                von{' '}
                <span className="font-semibold text-[#111418]">
                  {settleTarget.customer?.first_name}{' '}
                  {settleTarget.customer?.last_name}
                </span>
              </p>

              <div>
                <Label className="overline">Einbehaltener Betrag (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={settleTarget.amount}
                  value={settleForm.retained_amount}
                  onChange={(e) =>
                    setSettleForm({
                      ...settleForm,
                      retained_amount: e.target.value,
                    })
                  }
                  className="input-swiss"
                />

                <p className="text-xs text-[#8A94A6] mt-1">
                  Rückzahlung an Kunde:{' '}
                  {euro(
                    Math.max(
                      0,
                      Number(settleTarget.amount || 0) -
                        (parseFloat(settleForm.retained_amount) || 0)
                    )
                  )}
                </p>
              </div>

              <div>
                <Label className="overline">Grund (optional)</Label>
                <Input
                  value={settleForm.reason}
                  onChange={(e) =>
                    setSettleForm({
                      ...settleForm,
                      reason: e.target.value,
                    })
                  }
                  className="input-swiss"
                  placeholder="z.B. Lackschaden hinten links"
                />
              </div>

              <DepositInvoiceChoice deposit={settleTarget} form={settleForm} onChange={setSettleForm} />
            <label className="flex items-center gap-2 text-sm text-[#525866]">
                <input
                  type="checkbox"
                  checked={settleForm.create_invoice}
                disabled={!!settleForm.invoice_id}
                  onChange={(e) =>
                    setSettleForm({
                      ...settleForm,
                      create_invoice: e.target.checked,
                    })
                  }
                />
                Automatisch Rechnung über einbehaltenen Betrag erstellen
              </label>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSettleTarget(null)}
              disabled={busy}
            >
              Abbrechen
            </Button>

            <Button onClick={submitSettle} disabled={busy}>
              Entscheidung speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!refundTarget}
        onOpenChange={(open) => !open && setRefundTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rückzahlung dokumentieren</DialogTitle>
          </DialogHeader>

          {refundTarget && (
            <div className="space-y-4">
              <p className="text-sm text-[#525866]">
                Rückzahlung:{' '}
                <span className="font-semibold text-[#111418]">
                  {euro(refundTarget.refund_amount)}
                </span>{' '}
                an{' '}
                <span className="font-semibold text-[#111418]">
                  {refundTarget.customer?.first_name}{' '}
                  {refundTarget.customer?.last_name}
                </span>
              </p>

              <div>
                <Label className="overline">Zahlungsart</Label>
                <select
                  className="input-swiss w-full"
                  value={refundForm.payment_method}
                  onChange={(e) =>
                    setRefundForm({
                      ...refundForm,
                      payment_method: e.target.value,
                    })
                  }
                >
                  <option value="bar">Bar (Kassenbuch-Eintrag)</option>
                  <option value="ec">EC-Karte</option>
                  <option value="ueberweisung">Überweisung</option>
                </select>
              </div>

              <div>
                <Label className="overline">Hinweis (optional)</Label>
                <Input
                  value={refundForm.notes}
                  onChange={(e) =>
                    setRefundForm({
                      ...refundForm,
                      notes: e.target.value,
                    })
                  }
                  className="input-swiss"
                  placeholder="z.B. Referenznr., Überweisungsdatum"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRefundTarget(null)}
              disabled={busy}
            >
              Abbrechen
            </Button>

            <Button onClick={submitRefund} disabled={busy}>
              Rückzahlung dokumentieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Deposits;
